import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, StarIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
export function HeroSection() {
  return <section id="hero" className="relative min-h-screen w-full flex items-center justify-center overflow-hidden bg-navy" data-id="element-181">

      {/* Gradient orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-182">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] rounded-full animate-glow-pulse" style={{
        background: 'radial-gradient(ellipse at center, rgba(108, 92, 231, 0.25) 0%, rgba(200, 80, 192, 0.1) 40%, transparent 70%)'
      }} data-id="element-183" />

        <div className="absolute bottom-0 left-0 w-[600px] h-[400px] rounded-full" style={{
        background: 'radial-gradient(ellipse at center, rgba(200, 80, 192, 0.12) 0%, transparent 60%)'
      }} data-id="element-184" />

        <div className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full" style={{
        background: 'radial-gradient(ellipse at center, rgba(139, 92, 246, 0.1) 0%, transparent 60%)'
      }} data-id="element-185" />

      </div>

      {/* Grid pattern overlay */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
      backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
      backgroundSize: '60px 60px'
    }} data-id="element-186" />


      {/* Content */}
      <div className="relative z-10 max-w-4xl mx-auto px-6 text-center pt-24 pb-8" data-id="element-187">
        {/* Logo */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.8
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.5,
        delay: 0
      }} className="mb-8" data-id="element-188">

          <img src="https://thruline-public.s3.us-west-1.amazonaws.com/brand/logos/png/icon-dark-trans.png" alt="Thruline" className="w-16 h-16 mx-auto" data-id="element-189" />

        </motion.div>

        {/* Badge */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.1
      }} data-id="element-190">

          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-violet-electric/30 bg-violet-electric/10 text-violet-electric text-xs font-medium tracking-wide uppercase mb-8" data-id="element-191">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" data-id="element-192" />
            Now accepting new customers
          </span>
        </motion.div>

        {/* Headline */}
        <motion.h1 className="text-5xl sm:text-6xl md:text-7xl font-bold text-white leading-[1.05] tracking-tight mb-6" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} data-id="element-193">

          Custom ERP.
          <br data-id="element-194" />
          <span className="bg-gradient-to-r from-violet-electric via-vivid-purple to-magenta-hot bg-clip-text text-transparent" data-id="element-195">
            Built in 60 days.
          </span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p className="text-lg sm:text-xl text-white/60 max-w-2xl mx-auto mb-10 leading-relaxed" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.35
      }} data-id="element-196">

          Stop fighting your off-the-shelf ERP. Thruline builds fully custom
          enterprise software for manufacturing and operations — fixed pricing,
          delivered fast.
        </motion.p>

        {/* CTAs */}
        <motion.div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.5
      }} data-id="element-197">

          <a href="https://calendly.com/alex-thruline/learn-about-thruline" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-white text-navy font-semibold text-sm hover:bg-white/90 transition-all hover:shadow-lg hover:shadow-white/10" data-id="element-198">

            Get a Demo
            <ArrowRightIcon className="w-4 h-4" data-id="element-199" />
          </a>
          <Link to="/resources/thesis" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full border border-violet-electric/30 text-violet-electric font-medium text-sm hover:border-violet-electric/60 hover:bg-violet-electric/5 transition-all" data-id="element-200">

            Read Our Thesis
            <ArrowRightIcon className="w-4 h-4" data-id="element-201" />
          </Link>
        </motion.div>

        {/* Trust indicators */}
        <motion.div className="flex flex-col items-center gap-4" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8,
        delay: 0.7
      }} data-id="element-202">

          <div className="flex items-center gap-1" data-id="element-203">
            <StarIcon className="w-4 h-4 text-yellow-400 fill-yellow-400" data-id="element-204" />
            <StarIcon className="w-4 h-4 text-yellow-400 fill-yellow-400" data-id="element-205" />
            <StarIcon className="w-4 h-4 text-yellow-400 fill-yellow-400" data-id="element-206" />
            <StarIcon className="w-4 h-4 text-yellow-400 fill-yellow-400" data-id="element-207" />
            <StarIcon className="w-4 h-4 text-yellow-400 fill-yellow-400" data-id="element-208" />
          </div>
          <p className="text-white/40 text-sm" data-id="element-209">
            Trusted across manufacturing, biotech, and logistics
          </p>
        </motion.div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-navy to-transparent pointer-events-none" data-id="element-210" />
    </section>;
}